import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { useState, useRef, useCallback, useEffect, createElement } from 'react';
import { differenceInCalendarMonths } from 'date-fns';
import MonthBlock from './MonthBlock.js';
import { DatePickerWrapper, DatePickerHeader, DatePickerNav, DatePickerBody, DatePickerMonths } from './style.js';
import MonthHeader from './MonthHeader.js';
import Icon from '../Icon/Icon.js';
import getCheckedSiteId from '../../utils/siteId.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { USER_INTERFACE_ARROW_04_LEFT } from '../IconPath/USER_INTERFACE_ARROW_04_LEFT.js';
import { USER_INTERFACE_ARROW_04_RIGHT } from '../IconPath/USER_INTERFACE_ARROW_04_RIGHT.js';

var _excluded = ["startDate", "nrOfMonths", "selectedDate", "selectedMonths", "selectedDateInterval", "onChange", "scrollToSelected", "siteId"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function getFirstSelectedMonth(selectedMonths, selectedDate, selectedInterval) {
  // selectedMonths overrides the other two arguments so check it first.
  if (Array.isArray(selectedMonths) && selectedMonths.length) {
    // don't modify arguments
    var months = selectedMonths.slice();
    months.sort();
    try {
      return [parseInt(months[0].substr(0, 4), 10), parseInt(months[0].substr(4, 2), 10)];
    } catch (e) {
      // invalid format on date, ignore
      return null;
    }
  }
  if (selectedDate) {
    try {
      var n = parseInt(selectedInterval, 10) || 0;
      var d = new Date(selectedDate);
      // use selectedInterval to adjust startDate
      d.setDate(d.getDate() - n);
      return [d.getFullYear(), d.getMonth() + 1];
    } catch (e) {
      // invalid format on date, ignore
      return null;
    }
  }
  return null;
}
var isSmallScreen = function isSmallScreen() {
  return typeof window !== 'undefined' && window && window.innerWidth < 768;
};
var getMonthTopPosition = function getMonthTopPosition(container, monthIndex) {
  if (container && monthIndex >= 0) {
    var month = container.childNodes[monthIndex];
    if (month) {
      return month.offsetTop;
    }
  }
  return 0;
};
var useScrollToSelectedMonth = function useScrollToSelectedMonth(shouldScroll, startDate, selectedMonths, selectedDate, selectedDateInterval, listRef, scrollRef, navigateNext, monthWidth) {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasMadeInitialScroll = _useState2[0],
    setHasMadeInitialScroll = _useState2[1];
  useEffect(function () {
    // do this in useEffect hook to prevent it from running in ssr.
    if (monthWidth && shouldScroll && !hasMadeInitialScroll) {
      // We are only going to do this once, so we don't need to store anything in state.
      var firstSelectedMonth = getFirstSelectedMonth(selectedMonths, selectedDate, selectedDateInterval);
      if (firstSelectedMonth) {
        var _firstSelectedMonth = _slicedToArray(firstSelectedMonth, 2),
          y = _firstSelectedMonth[0],
          m = _firstSelectedMonth[1];
        var index = differenceInCalendarMonths(new Date(y, m - 1, 1), startDate);
        var smallScreen = isSmallScreen();
        if (index >= 0) {
          if (smallScreen) {
            // vertical scroll
            var scrollTop = getMonthTopPosition(listRef.current, index);
            if (scrollRef.current) {
              scrollRef.current.scrollTop = scrollTop;
            }
          } else {
            // horizontal scroll
            navigateNext(null, index);
          }
        }
      }

      // so it only runs once.
      setHasMadeInitialScroll(true);
    }
  }, [shouldScroll, hasMadeInitialScroll, listRef.current, navigateNext, monthWidth]);
};
var DatePicker = function DatePicker(props) {
  var startDate = props.startDate,
    nrOfMonths = props.nrOfMonths,
    selectedDate = props.selectedDate,
    selectedMonths = props.selectedMonths,
    selectedDateInterval = props.selectedDateInterval,
    onChange = props.onChange,
    scrollToSelected = props.scrollToSelected,
    siteId = props.siteId,
    rest = _objectWithoutProperties(props, _excluded);
  var date = new Date(startDate);
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    currentLeft = _useState4[0],
    setLeftPos = _useState4[1];
  var listRef = useRef(null);
  var scrollContainer = useRef(null);
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    monthWidth = _useState6[0],
    setMonthWidth = _useState6[1];
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    containerWidth = _useState8[0],
    setContainerWidth = _useState8[1];
  var maxCurrentLeft = nrOfMonths * monthWidth * -1 + (containerWidth || 0);
  var navigatePrev = useCallback(function () {
    setLeftPos(function (prev) {
      return prev + monthWidth;
    });
  }, [monthWidth]);
  var navigateNext = useCallback(function (e) {
    var n = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
    setLeftPos(function (prev) {
      return Math.max(prev - n * monthWidth, maxCurrentLeft);
    });
  }, [monthWidth, maxCurrentLeft]);
  useEffect(function () {
    if (listRef.current && listRef.current.firstChild) {
      setMonthWidth(listRef.current.firstChild.offsetWidth);
    }
    if (scrollContainer.current) {
      setContainerWidth(scrollContainer.current.offsetWidth);
    }
  }, [listRef.current, scrollContainer.current]);
  useScrollToSelectedMonth(scrollToSelected, date, selectedMonths, selectedDate, selectedDateInterval, listRef, scrollContainer, navigateNext, monthWidth);
  var months = [];
  var selectedDateObj = new Date(selectedDate);
  var startMonth = date.getMonth();
  var startYear = date.getFullYear();
  for (var index = 0; index < nrOfMonths; index += 1) {
    var monthIndex = startMonth + index;
    var currentMonth = monthIndex % 12;
    var addYear = Math.floor(monthIndex / 12);
    var year = new Date(startYear + addYear, currentMonth, 1).getFullYear();
    var isWithinSelected = selectedDateObj.getFullYear() === year && selectedDateObj.getMonth() === currentMonth;
    months.push( /*#__PURE__*/createElement(MonthBlock, _objectSpread(_objectSpread({}, rest), {}, {
      siteId: getCheckedSiteId(siteId),
      selectedDate: isWithinSelected ? selectedDate : null,
      selectedMonths: selectedMonths,
      selectedDateInterval: selectedDateInterval,
      onChange: onChange,
      key: "".concat(year, "-").concat(currentMonth),
      month: currentMonth,
      year: year
    })));
  }
  return /*#__PURE__*/jsxs(DatePickerWrapper, {
    children: [/*#__PURE__*/jsxs(DatePickerHeader, {
      children: [/*#__PURE__*/jsx(MonthHeader, {
        siteId: getCheckedSiteId(siteId)
      }), /*#__PURE__*/jsx(DatePickerNav, {
        hidden: currentLeft >= 0,
        onClick: navigatePrev,
        className: "sg-datepicker__nav-prev",
        type: "button",
        children: /*#__PURE__*/jsx(Icon, {
          path: USER_INTERFACE_ARROW_04_LEFT,
          size: "xs"
        })
      }), /*#__PURE__*/jsx(DatePickerNav, {
        hidden: currentLeft <= maxCurrentLeft,
        onClick: navigateNext,
        className: "sg-datepicker__nav-next",
        type: "button",
        children: /*#__PURE__*/jsx(Icon, {
          path: USER_INTERFACE_ARROW_04_RIGHT,
          size: "xs"
        })
      })]
    }), /*#__PURE__*/jsx(DatePickerBody, {
      ref: scrollContainer,
      children: /*#__PURE__*/jsx(DatePickerMonths, {
        style: {
          transform: "translateX(".concat(currentLeft, "px)")
        },
        ref: listRef,
        children: months
      })
    })]
  });
};
DatePicker.defaultProps = {
  siteId: 1,
  nrOfMonths: 3,
  validDates: [],
  selectedDate: '',
  selectedDateInterval: {},
  selectedMonths: [],
  onChange: function onChange() {},
  monthFooter: function monthFooter() {},
  scrollToSelected: true,
  disableHistoricalDates: true
};
var DatePicker$1 = DatePicker;

export { DatePicker$1 as default, getFirstSelectedMonth };
//# sourceMappingURL=DatePicker.js.map
